import { useBoolean } from '@blockworks/platform/client';
import { Button, ExclamationPointCircleIcon, FlexBox, Link, LinkExternalIcon, List, Modal, Text } from '@blockworks/ui';
import { Bit } from '@blockworks/ui/factory';
import { cn } from '@blockworks/ui/system';

import { VisualizationError, VisualizationErrorType } from '../../models';
import { ValidationError } from '../../workflows/validation';

const VisualizationErrorMessage = ({ error }: { error: VisualizationError }) => {
    const modal = useBoolean();

    const renderError = () => {
        if (error?.type === VisualizationErrorType.Schema && error?.details?.length) {
            return (
                <Button intent="neutral" size="xs" center onClick={modal.setTrue}>
                    View {error.details?.length} Errors
                </Button>
            );
        }

        return <Text size="xs">{error.message}</Text>;
    };

    if (!error) {
        return null;
    }

    return (
        <FlexBox alignItems="center" justifyContent="center" gap={2} h="full" w="full">
            <FlexBox col gap="xs" px={8} w="full" maxW={800} maxH="full" display="flex" center>
                <FlexBox
                    gap={2}
                    col={error.type === VisualizationErrorType.Schema}
                    center
                    className="bg-surface-depth"
                    p={2}
                    px={4}
                    borderRadius="md"
                >
                    <ExclamationPointCircleIcon
                        className={cn(
                            {
                                'h-8 w-8': error.type === VisualizationErrorType.Configuration,
                                'h-12 w-12': error.type === VisualizationErrorType.Schema,
                            },
                            'text-content-deselected',
                        )}
                    />
                    {renderError()}
                </FlexBox>
            </FlexBox>
            <Modal open={modal.value} onOpenChange={modal.setValue}>
                <Modal.Header>
                    <Modal.Title>Visualization Errors</Modal.Title>
                </Modal.Header>
                <Modal.Content>
                    <FlexBox py={1} px={3} alignItems="center" gap="xs">
                        <Link target="_blank" href={`/visualizations/${error.visualizationId}/edit`}>
                            <Bit.div
                                gap={1}
                                alignItems="center"
                                display="flex"
                                className="text-primary"
                                borderRadius="md"
                            >
                                Visualization {error.visualizationId} <LinkExternalIcon className="w-3 h-3" />
                            </Bit.div>
                        </Link>
                        <Link target="_blank" href={`/queries/${error.queryId}/edit`}>
                            <Bit.div
                                gap={1}
                                alignItems="center"
                                display="flex"
                                className="text-primary"
                                borderRadius="md"
                            >
                                Query {error.queryId} <LinkExternalIcon className="w-3 h-3" display="inline-block" />
                            </Bit.div>
                        </Link>
                    </FlexBox>
                    <Bit.div className="max-h-[calc(100vh-100px)] h-[400px] w-[600px] max-w-[90vw] p-2">
                        <Bit.div className="h-[400px] max-h-[calc(100%-32px)]">
                            <List rowHeight={32} h={32 * 12} variant="compact" divided>
                                <List.Row type="header">
                                    <List.Col size="md">Field</List.Col>
                                    <List.Col size="xl" grow>
                                        Message
                                    </List.Col>
                                </List.Row>
                                {error.details?.map?.((err: ValidationError) => {
                                    return (
                                        <List.Row key={err.message}>
                                            <List.Col size="md">{err.field}</List.Col>
                                            <List.Col size="xl" grow>
                                                {err.message}
                                            </List.Col>
                                        </List.Row>
                                    );
                                })}
                            </List>
                        </Bit.div>
                    </Bit.div>
                </Modal.Content>
            </Modal>
        </FlexBox>
    );
};

export { VisualizationErrorMessage };
